/* eslint-disable import/no-cycle */
import React, { Component, createRef } from 'react'

import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { validateRecaptcha, clearValidateRecaptcha } from 'actions/reCaptcha'
import { getValidateRecaptchaData } from 'selectors/reCaptcha'

import RecaptchaErrorMessage from './recaptchaErrorMessage'

const noop = () => undefined

/*
 * This component base code was taken from 'react-recaptcha-google' lib.
 * While it was made with ReCaptcha V2 in mind, it since has been updated for reCaptcha Enterprise.
 * Defaults have been changed to use invisible recaptchas on form submits.
 * (Devs can still override these defaults by passing in props for custom behaviour)
 * The only required props are ref and onSuccess function.
 *   <ReCaptcha ref={recaptchaRef} onSuccess={() => formik.handleSubmit(formik.values)} />
 * Your form's onSubmit function should preventDefault and call the recaptchaRef.current.execute() function.
 *   <form onSubmit={(e) => { e.preventDefault(); recaptchaRef.current.execute() }}>
 * onSuccess should handle your original form submission code.
 *
 * The purpose of using invisible reCaptcha badges is to verify the user upon a certain action they take.
 * An example of this is when a user submits a form.
 * When possible, use the recaptchas isFetching selector to bring up a loading spinner.
 * Because verification can take an extra few seconds, showing something ASAP creates better UX.
 */
class ReCaptcha extends Component {
  static defaultProps = {
    badge: 'bottomright',
    inherit: true,
    isolated: false,
    onSuccess: noop,
    size: 'invisible',
    sitekey: process.env.RAZZLE_RECAPTCHA_CLIENT_KEY,
    tabIndex: 0,
    theme: 'light',
    type: 'image',
    render: 'explicit'
  }
  static propTypes = {
    badge: PropTypes.oneOf(['bottomright', 'bottomleft', 'inline']),
    inherit: PropTypes.bool,
    isolated: PropTypes.bool,
    onError: PropTypes.func,
    onExpired: PropTypes.func,
    onLoad: PropTypes.func,
    onSuccess: PropTypes.func,
    sitekey: PropTypes.string,
    size: PropTypes.oneOf(['compact', 'normal', 'invisible']),
    tabIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    theme: PropTypes.oneOf(['dark', 'light']),
    otherDomDataAttrs: PropTypes.object
  }
  isReady = () =>
    typeof window !== 'undefined' &&
    typeof window.grecaptcha !== 'undefined' &&
    typeof window.grecaptcha.enterprise !== 'undefined' &&
    typeof window.grecaptcha.enterprise.render === 'function'

  readyIntervalId = setInterval(() => this._updateReadyState(), 1000)
  recaptcha = createRef()

  state = {
    ready: this.isReady()
  }

  TWO_MINUTES = 120000

  componentWillUnmount() {
    clearInterval(this.readyIntervalId)
  }

  componentDidMount() {
    if (this.state.ready) {
      this.widgetId = this.grecaptchaRender()
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.ready && this.state.ready) {
      this.widgetId = this.grecaptchaRender()
    }
  }

  grecaptchaRender = () => {
    try {
      return window.grecaptcha.enterprise.render(
        this.recaptcha.current,
        {
          'error-callback': this.handleOnError,
          'expired-callback': this.handleOnExpire,
          badge: this.props.badge,
          callback: this.verifyCallback,
          isolated: this.props.isolated,
          sitekey: this.props.sitekey,
          size: this.props.size,
          tabindex: this.props.tabIndex,
          theme: this.props.theme
        },
        this.props.inherit
      )
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error)
    }
  }

  verifyCallback = token => {
    this.props.validateRecaptcha(token, this.props.onSuccess)
  }

  handleOnExpire = () => {
    this.props.clearValidateRecaptcha()
    this.props.onExpired && this.props.onExpired()
    this.reset()
  }

  handleOnError = () => {
    this.props.onError && this.props.onError()
    this.reset()
  }

  reset = () => {
    window.grecaptcha.enterprise.reset(this.widgetId)
  }

  isRecaptchaExpired = () => {
    const recaptchaData = this.props.reCaptchaValidationData

    if (!recaptchaData) return true
  
    const validationTime = new Date(recaptchaData.validationTime)
    const currentTime = new Date()
    const difference = currentTime - validationTime
  
    return difference >= this.TWO_MINUTES
  }

  // Invokes the reCAPTCHA check which runs the assigned callback function.
  execute = () => {
    // Avoids doing extra verifications if token is already verified.
    this.isRecaptchaExpired()
      ? window.grecaptcha.enterprise.execute(this.widgetId)
      : this.props.onSuccess()
  }

  _updateReadyState = () => {
    if (this.isReady()) {
      this.setState(() => ({
        ready: true
      }))
      clearInterval(this.readyIntervalId)
      this.props.onLoad && this.props.onLoad()
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !this.state.ready && nextState.ready
  }

  render() {
    const { sitekey, theme, type, size, badge, tabIndex } = this.props

    return (
      <>
        <RecaptchaErrorMessage />
        <div
          ref={this.recaptcha}
          data-sitekey={sitekey}
          data-theme={theme}
          data-type={type}
          data-size={size}
          data-badge={badge}
          data-tabindex={tabIndex}
          style={{ position: 'relative', zIndex: 100 }} // Assures things like the badge are on top of other elements
        />
      </>
    )
  }
}

const mapStateToProps = state => ({
  reCaptchaValidationData: getValidateRecaptchaData(state)
})

const mapDispatchToProps = {
  validateRecaptcha,
  clearValidateRecaptcha
}

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(ReCaptcha)
